import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

/* ── Global GET cache ─────────────────────────────────────────────────────── */
const globalGetCache = {};
const CACHE_TTL = 300000; // 5 minutes

export function clearGlobalGetCache() {
  for (const key in globalGetCache) {
    delete globalGetCache[key];
  }
}

/* ── Global logout flag ─────────────────────────────────────────────────────
   Set this to true BEFORE clearing localStorage during logout.
   Prevents the 401 interceptor from firing hundreds of times on all
   in-flight polling requests, which causes the "Page Unresponsive" freeze.
   ────────────────────────────────────────────────────────────────────────── */
let _isLoggingOut = false;

export function setLoggingOut() {
  _isLoggingOut = true;
}

const apiClient = axios.create({
  baseURL: BASE_URL, // GLOBAL backend root
  withCredentials: true, // Crucial to send HttpOnly cookies with every request
});

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + "=")) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

/* Attach CSRF token automatically and bypass browser cache for GETs */
apiClient.interceptors.request.use((config) => {
  const isLogout = config.url && (config.url.includes("/accounts/api/logout/") || config.url.endsWith("/accounts/api/logout"));
  if (_isLoggingOut && !isLogout) {
    const controller = new AbortController();
    controller.abort();
    return { ...config, signal: controller.signal };
  }

  // Force bypass of browser caching for all GET requests
  if (config.method && config.method.toLowerCase() === "get") {
    if (config.params instanceof URLSearchParams) {
      config.params.set("_t", Date.now().toString());
    } else {
      config.params = config.params || {};
      config.params._t = Date.now();
    }

    // Cache lookup: check if cached response exists
    const isChat = config.url && config.url.includes("/chat/");
    const isAuthMe = config.url && config.url.includes("/accounts/api/auth/me/");
    if (!isChat && !isAuthMe) {
      const paramsCopy = { ...(config.params || {}) };
      delete paramsCopy._t;
      const cacheKey = config.url + JSON.stringify(paramsCopy);
      const cached = globalGetCache[cacheKey];
      const now = Date.now();
      
      if (cached && (now - cached.timestamp < CACHE_TTL)) {
        // Return cached response instantly in-memory, avoiding network call
        config.adapter = () => {
          return Promise.resolve({
            data: JSON.parse(JSON.stringify(cached.data)),
            status: 200,
            statusText: "OK",
            headers: {},
            config: config,
          });
        };
      }
    }
  } else {
    // If it's a mutation (POST, PUT, PATCH, DELETE), invalidate the cache
    clearGlobalGetCache();
  }

  // Attach X-CSRFToken header for unsafe requests
  const isSafeMethod = /^(GET|HEAD|OPTIONS|TRACE)$/i.test(config.method);
  if (!isSafeMethod) {
    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
      config.headers["X-CSRFToken"] = csrfToken;
    }
  }

  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/* Auto refresh on 401 & retry original request */
apiClient.interceptors.response.use(
  (res) => {
    // Cache successful GET responses
    const config = res.config;
    if (config && config.method && config.method.toLowerCase() === "get") {
      const isChat = config.url && config.url.includes("/chat/");
      const isAuthMe = config.url && config.url.includes("/accounts/api/auth/me/");
      if (!isChat && !isAuthMe) {
        const paramsCopy = { ...(config.params || {}) };
        delete paramsCopy._t;
        const cacheKey = config.url + JSON.stringify(paramsCopy);
        globalGetCache[cacheKey] = {
          data: res.data,
          timestamp: Date.now(),
        };
      }
    }
    return res;
  },
  async (error) => {
    const originalRequest = error.config;
    const isLoginRequest = originalRequest?.url && originalRequest.url.includes("/accounts/api/login/");
    const isRefreshRequest = originalRequest?.url && originalRequest.url.includes("/accounts/api/token/refresh/");

    if (_isLoggingOut) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !isLoginRequest && !isRefreshRequest && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await apiClient.post("/accounts/api/token/refresh/");
        isRefreshing = false;
        processQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError);

        _isLoggingOut = true;
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 401 && (isLoginRequest || isRefreshRequest)) {
      if (isRefreshRequest) {
        _isLoggingOut = true;
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
